/**
 * Tabel-georiënteerde Onderwater-venster (vervangt op termijn TextPane).
 *
 * Conceptueel model (per gebruiker-ontwerp): elke rij IS één concept of
 * relatie. Kolommen zijn projecties van die row:
 *
 *   B — Tekstuele representatie  (`ApplicationComponent(Customer, "Customer")`)
 *   C — Positie / source-handle  (x,y voor concept; "left"/"top" voor relatie)
 *   D — Target-handle            (alleen voor relaties)
 *   E — Anker                    (concept-id of relation-id; read-only)
 *
 * Kolom A (raw PlantUML) is bewust weggesneden — was pure projectie zonder
 * eigen informatie. PlantUML-render gebeurt straks rij-voor-rij in de
 * preview-pane. Plak-PlantUML-input komt in een aparte text-area, niet als
 * kolom per rij.
 *
 * Iteratie 2 (deze versie): kolom B name/label-edit. Dubbelklik op de
 * tekstuele cel opent een input met alleen het naam/label-deel; Enter /
 * blur committen via `patchConcept` of `patchRelation`. Esc cancelt.
 * C/D/E blijven read-only — komt in iteratie 3.
 */

import type { ViewElementState } from "@ae/domain";
import { parseArchiMatePlantUML } from "@ae/domain";
import type { JSX } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { getRegistry, resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { handleIdToAngle } from "../canvas/angleUtils";
import { confirmHardDelete } from "../cascadeConfirm";
import { applyParseResult } from "../text-pane/applyParseResult";
import { PreviewPane } from "../text-pane/PreviewPane";
import { RevealPaneHeaderControls } from "../text-pane/RevealPaneHeaderControls";
import { useRevealEmitters } from "../text-pane/revealText";
import { diagramEntryKeysForRows } from "./diagramRemoval";
import {
  findEmptyPlacement,
  NEW_NODE_DEFAULT_HEIGHT,
  NEW_NODE_DEFAULT_WIDTH,
} from "./findEmptyPlacement";
import { InsertRowDialog, type RelationEndpoint } from "./InsertRowDialog";

/**
 * Eén rij in de Onderwater-tabel. `kind` discrimineert tussen concept en
 * relation zodat downstream-componenten (cell-render, edit-handler) er
 * op kunnen schakelen zonder type-narrowing met `in`-checks.
 */
type GridRow =
  | {
      kind: "concept";
      anchorId: string; // concept-id
      typeShort: string; // typeId zonder `archimate.`-prefix
      typeId: string; // volledige typeId voor stereotype-lookup
      name: string; // bewerkbaar deel
      label: ""; // concepten hebben geen relatie-label
      sourceName: ""; // concepten hebben geen source/target
      targetName: "";
      position: { x: number; y: number } | null;
      stereotypes: string[]; // bewerkbaar via kolom E
    }
  | {
      kind: "relation";
      anchorId: string; // relation-id
      typeShort: string;
      typeId: string;
      name: ""; // relaties hebben geen eigen naam
      label: string; // bewerkbaar
      sourceName: string; // read-only, weergave-only
      targetName: string;
      /**
       * Weergave-vorm van endpoint-attachment. Drie scenario's:
       *  - `<handle-id>` (bv. "left", "top") wanneer expliciet gezet
       *  - `~<n>°` wanneer alleen `sourceAngle`/`targetAngle` is gezet
       *  - `auto` wanneer geen van beide → React Flow routet automatisch
       */
      sourceAttach: string;
      targetAttach: string;
      stereotypes: string[];
    };

/**
 * Map een hoek terug naar 'n canonieke handle-naam wanneer 'm precies
 * op een van de vier vaste assen ligt. Tolerantie ±5° dekt floating-
 * point-roundtrip-drift af.
 */
function angleToCanonicalHandle(angleDeg: number): string | null {
  const a = ((angleDeg % 360) + 360) % 360;
  if (Math.abs(a - 0) < 5 || Math.abs(a - 360) < 5) return "right";
  if (Math.abs(a - 90) < 5) return "bottom";
  if (Math.abs(a - 180) < 5) return "left";
  if (Math.abs(a - 270) < 5) return "top";
  // Corner-handles (45/135/225/315° ± 5°) sinds de hoek-aanhechtpunten-
  // feature (2026-05-26).
  if (Math.abs(a - 45) < 5) return "bottom-right";
  if (Math.abs(a - 135) < 5) return "bottom-left";
  if (Math.abs(a - 225) < 5) return "top-left";
  if (Math.abs(a - 315) < 5) return "top-right";
  return null;
}

/**
 * Formatteer een endpoint-attachment voor weergave in kolom C/D.
 * Voorkeur: expliciet handle > canonieke hoek (= expliciete keuze,
 * geen `~`-prefix) > niet-canonieke hoek > geometrische benadering.
 * PlantUML's `@handles`-syntax accepteert beide vormen.
 */
function formatAttachment(
  explicitHandle: string | undefined,
  explicitAngle: number | undefined,
  approxHandle: string | null,
): string {
  if (explicitHandle) return explicitHandle;
  if (typeof explicitAngle === "number") {
    const canonical = angleToCanonicalHandle(explicitAngle);
    if (canonical) return canonical;
    return `~${Math.round(explicitAngle)}°`;
  }
  if (approxHandle) return `~${approxHandle}`;
  return "auto";
}

/**
 * Default node-grootte als `viewState.width`/`height` niet expliciet
 * is gezet. Stem ongeveer overeen met wat ArchiMate-rechthoeken in
 * de canvas-renderer krijgen — exacte waardes zijn voor handle-
 * detectie weinig kritisch (alleen quadrant-detectie telt).
 */
const DEFAULT_NODE_WIDTH = 140;
const DEFAULT_NODE_HEIGHT = 60;

/**
 * Bouwt een map van element-id → absolute (x, y, w, h) op het diagram.
 * Voor genest-in-container concepten worden parent-offsets meegerekend
 * door de container-chain te wandelen. Zonder dit zouden cross-
 * container-relaties verkeerd-gequadranteerd worden.
 */
function buildAbsoluteRects(
  viewState: Record<
    string,
    {
      position?: { x: number; y: number } | undefined;
      width?: number | undefined;
      height?: number | undefined;
      containerId?: string | undefined;
    }
  >,
): Map<string, { x: number; y: number; w: number; h: number }> {
  const out = new Map<string, { x: number; y: number; w: number; h: number }>();
  const seenStack = new Set<string>();
  const resolve = (id: string): { x: number; y: number; w: number; h: number } | null => {
    const cached = out.get(id);
    if (cached) return cached;
    if (seenStack.has(id)) return null; // cyclische containment-protectie
    const ve = viewState[id];
    if (!ve?.position) return null;
    seenStack.add(id);
    let baseX = ve.position.x;
    let baseY = ve.position.y;
    if (ve.containerId) {
      const parent = resolve(ve.containerId);
      if (parent) {
        baseX += parent.x;
        baseY += parent.y;
      }
    }
    seenStack.delete(id);
    const rect = {
      x: baseX,
      y: baseY,
      w: ve.width ?? DEFAULT_NODE_WIDTH,
      h: ve.height ?? DEFAULT_NODE_HEIGHT,
    };
    out.set(id, rect);
    return rect;
  };
  for (const id of Object.keys(viewState)) resolve(id);
  return out;
}

/**
 * Bepaal welke handle (`left`/`right`/`top`/`bottom`) een endpoint
 * naar verwachting krijgt op basis van de quadrant tussen source-
 * en target-centrum. Spiegelt React Flow's interne auto-routing-
 * heuristic, dus de getoonde waarde komt overeen met wat de modelleur
 * visueel op canvas ziet (modulo edge-cases zoals zelf-relaties).
 *
 * Returnt `null` als één van beide endpoints geen position heeft.
 */
function approxHandlesFromGeometry(
  sourceRect: { x: number; y: number; w: number; h: number } | undefined,
  targetRect: { x: number; y: number; w: number; h: number } | undefined,
): { sourceHandle: string; targetHandle: string } | null {
  if (!sourceRect || !targetRect) return null;
  const sCx = sourceRect.x + sourceRect.w / 2;
  const sCy = sourceRect.y + sourceRect.h / 2;
  const tCx = targetRect.x + targetRect.w / 2;
  const tCy = targetRect.y + targetRect.h / 2;
  const dx = tCx - sCx;
  const dy = tCy - sCy;
  // Bias naar horizontaal wanneer dx en dy ongeveer gelijk zijn — dat
  // strookt met React Flow's default tie-breaker.
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0
      ? { sourceHandle: "right", targetHandle: "left" }
      : { sourceHandle: "left", targetHandle: "right" };
  }
  return dy >= 0
    ? { sourceHandle: "bottom", targetHandle: "top" }
    : { sourceHandle: "top", targetHandle: "bottom" };
}

/**
 * `archimate.`-prefix wegstrippen — die laag wordt impliciet aangenomen
 * als ArchiMate-base, dus weergave-tekst is leesbaarder zonder. Andere
 * namespaces (`c4.`, `sharp.`, `risk.`, `drawing.`, etc.) blijven
 * expliciet zichtbaar zodat de modelleur ziet welke pack 't levert.
 */
function shortTypeId(typeId: string): string {
  return typeId.startsWith("archimate.") ? typeId.slice("archimate.".length) : typeId;
}

/**
 * Junction-types (And/Or) hebben in de praktijk vrijwel altijd een lege
 * naam — ze representeren een logische connector tussen relaties, niet
 * 'n eigenstandig concept met label. In de tabel zou dat anders als
 * leeg-na-komma verschijnen (`Realization(Smartcard, )`), wat de lezer
 * verwart. Vervangen door een taal-aware placeholder zodat de
 * boolean-aard expliciet zichtbaar is:
 *
 *   nl:  <en> / <of>
 *   en:  <and> / <or>
 *
 * Junctions met WEL een naam (zeldzaam — bv. "guard-check") tonen die
 * gewone naam ongewijzigd. Alle andere concepten zonder naam vallen
 * elders terug op `(unnamed)`-placeholder.
 */
function displayConceptName(
  concept: { typeId: string; name: Record<string, string> },
  lang: "nl" | "en",
): string {
  // Junction-check eerst — `resolveBilingual` valt voor lege bilingual-
  // bags terug op `(unnamed)`, wat we hier juist NIET willen tonen.
  const hasName =
    (concept.name[lang] ?? "").trim() !== "" ||
    (concept.name.nl ?? "").trim() !== "" ||
    (concept.name.en ?? "").trim() !== "";
  if (!hasName) {
    if (concept.typeId === "archimate.AndJunction") {
      return lang === "nl" ? "<en>" : "<and>";
    }
    if (concept.typeId === "archimate.OrJunction") {
      return lang === "nl" ? "<of>" : "<or>";
    }
  }
  return resolveBilingual(concept.name, lang);
}

/**
 * Render het type-call-deel van een rij (kolom B). Concepten worden
 * weergegeven als `Type(Name)` of `Type((unnamed))` als de naam leeg is.
 * Relaties krijgen `Type(Source, Target[, "label"])`.
 *
 * Tekst is alleen-lezen-weergave; de edit-input toont straks puur 't
 * bewerkbare deel (de naam voor concepten, het label voor relaties).
 */
function renderRowText(row: GridRow): string {
  if (row.kind === "concept") {
    const n = row.name || "(unnamed)";
    return `${row.typeShort}(${n})`;
  }
  const labelPart = row.label ? `, "${row.label}"` : "";
  return `${row.typeShort}(${row.sourceName}, ${row.targetName}${labelPart})`;
}

/**
 * Compose een uniek row-id voor multi-select-tracking. Concept- en
 * relation-ids kunnen in zeldzame gevallen botsen (zou niet mogen via
 * UUID-generatie, maar defensief), dus prefix per kind.
 */
function rowKey(row: GridRow): string {
  return `${row.kind}:${row.anchorId}`;
}

export function GridPane() {
  const { i18n, t } = useTranslation();
  const lang: "nl" | "en" = i18n.language === "en" ? "en" : "nl";
  // Reveal-pane state — gedeeld met TextPane. Bepaalt of de live-preview
  // rechts naast de tabel verschijnt + welke render-modus (PlantUML /
  // Mermaid / SVG) actief is. De RevealPaneHeaderControls (rechts in de
  // header) muteert deze state via useUi-actions.
  const revealPreviewOpen = useUi((s) => s.revealPreviewOpen);
  const reveal = useRevealEmitters(lang);
  const revision = useWorkspaceData((s) => s.revision);
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  // Alleen kijken: de tabel is te lezen, te doorzoeken en te selecteren;
  // invoegen, bewerken, verplaatsen en wissen niet.
  const readOnly = useSession((s) => s.readOnly);
  const selectedConceptId = useUi((s) => s.selectedConceptId);
  const selectedRelationId = useUi((s) => s.selectedRelationId);
  const selectConcept = useUi((s) => s.selectConcept);
  const selectRelation = useUi((s) => s.selectRelation);
  const setSelectedConceptIds = useUi((s) => s.setSelectedConceptIds);
  const setSelectedRelationIds = useUi((s) => s.setSelectedRelationIds);
  // Multi-sets uit useUi — abonnement zodat we mee-clearen wanneer een
  // externe actie (m.n. canvas pane-click via selectConcept(null)) ze
  // op leeg zet. Anders zou de tabel geel-gehighlighte rijen blijven
  // tonen terwijl het canvas al gedeselecteerd is.
  const uiSelectedConceptIds = useUi((s) => s.selectedConceptIds);
  const uiSelectedRelationIds = useUi((s) => s.selectedRelationIds);

  /**
   * Multi-row-selectie. Set van `kind:anchorId`-keys. Bestaat naast de
   * UI-store-selectie (selectedConceptId / selectedRelationId): voor
   * canvas-sync gebruiken we de laatste *enkelvoudige* klik, voor
   * delete/copy-pasta gebruiken we deze multi-set. Excel-stijl:
   *   - Plain click: vervang selectie (single-select)
   *   - Cmd/Ctrl+click: toggle dit id in set
   *   - Shift+click: range-select van laatste-anchor t/m huidige
   */
  const [selectedRows, setSelectedRowsRaw] = useState<Set<string>>(new Set());

  /**
   * Mutator-wrapper voor `selectedRows`. Naast de lokale state-update
   * pusht 'ie de overeenkomstige concept-ids + relation-ids naar useUi
   * zodat canvas hand-in-hand mee-selecteert. We doen dit hier in plaats
   * van in een aparte forward-push useEffect omdat:
   *  1. Strict-mode runt elke useEffect 2× op mount, wat de "skip-first-
   *     run"-guard ondermijnt — bij de tweede run zou de effect met
   *     stale empty `selectedRows` per ongeluk de externe state wipen.
   *  2. Een directe wrapper is conceptueel duidelijker: élke user-actie
   *     die `selectedRows` muteert, weet expliciet dat 'ie OOK
   *     `useUi.selectedConceptIds`/`selectedRelationIds` moet bijwerken.
   *  3. De backward-sync (van useUi → local) gebruikt setSelectedRowsRaw
   *     direct, dus pull-uit-useUi triggert geen push-terug-naar-useUi.
   *
   * Een no-op-detectie voorkomt dat we useUi muteren wanneer de set
   * inhoudelijk niet wijzigt (= zelfde keys, ongeacht insertion-order).
   */
  const setSelectedRows = (next: Set<string>) => {
    setSelectedRowsRaw(next);
    const conceptIds: string[] = [];
    const relationIds: string[] = [];
    for (const key of next) {
      const [kind, ...rest] = key.split(":");
      const anchorId = rest.join(":");
      if (kind === "concept") conceptIds.push(anchorId);
      else if (kind === "relation") relationIds.push(anchorId);
    }
    const ui = useUi.getState();
    const sameConcepts =
      conceptIds.length === ui.selectedConceptIds.length &&
      conceptIds.every((id) => ui.selectedConceptIds.includes(id));
    const sameRelations =
      relationIds.length === ui.selectedRelationIds.length &&
      relationIds.every((id) => ui.selectedRelationIds.includes(id));
    if (!sameConcepts) setSelectedConceptIds(conceptIds);
    if (!sameRelations) setSelectedRelationIds(relationIds);
  };
  // Laatste rij die geklikt werd zonder modifier, voor shift-range-anchor.
  const [lastAnchor, setLastAnchor] = useState<string | null>(null);
  // Toetsenbord-cursor (rij-index). Onafhankelijk van selectie:
  // Cmd+Pijl beweegt de cursor zonder selectie aan te raken; plain Pijl
  // verplaatst + single-selecteert; Shift+Pijl strekt range vanaf
  // lastAnchor uit. -1 = geen cursor.
  const [cursorIdx, setCursorIdx] = useState<number>(-1);

  // Refs op de tbody-rijen voor scroll-into-view bij Pijl-navigatie.
  // Map van cursor-idx → element. Refs vullen we via een callback-ref
  // op elke <tr> tijdens render.
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  useEffect(() => {
    if (cursorIdx < 0) return;
    const el = rowRefs.current.get(cursorIdx);
    if (el) el.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [cursorIdx]);

  // (Forward-push is verhuisd naar de `setSelectedRows`-wrapper
  // hierboven. Geen aparte useEffect meer nodig: élke user-actie die
  // `selectedRows` muteert pusht direct naar useUi. Voorkomt
  // StrictMode-double-mount-race waarbij een initiele empty-push de
  // externe state wist die canvas vóór GridPane-mount had gevuld.)

  // **Unified external→local sync.** Spiegelt elke externe selectie-
  // bron (canvas single-click, canvas rubber-band, Explorer, hover-
  // tooltip-click) naar onze lokale `selectedRows`. Prioriteit:
  //
  //   1. Externe multi-sets (selectedConceptIds + selectedRelationIds)
  //      gevuld → spiegel die complete sets.
  //   2. Anders externe single-select gevuld → spiegel die ene rij.
  //   3. Anders → wis local (alles gedeselecteerd).
  //
  // CRITICAL: lees useUi via `getState()` voor de **fresh** waardes —
  // niet de gesubscribede snapshot. Reden: dit effect deelt z'n
  // deps-array met de forward-push useEffect die hierboven staat;
  // wanneer selectedRows lokaal verandert (bv. Shift-click), firet
  // forward-push eerst en update Zustand. Backward-sync firet daarna
  // in dezelfde tick, maar React's subscriber-snapshot kent de
  // ge-pushde waardes nog niet — zonder getState zou dit effect dan
  // local terug-overschrijven met stale "external"-state.
  useEffect(() => {
    void uiSelectedConceptIds;
    void uiSelectedRelationIds;
    void selectedConceptId;
    void selectedRelationId;
    const ui = useUi.getState();
    const externalKeys = new Set<string>();
    if (ui.selectedConceptIds.length > 0 || ui.selectedRelationIds.length > 0) {
      for (const id of ui.selectedConceptIds) externalKeys.add(`concept:${id}`);
      for (const id of ui.selectedRelationIds) externalKeys.add(`relation:${id}`);
    } else if (ui.selectedConceptId) {
      externalKeys.add(`concept:${ui.selectedConceptId}`);
    } else if (ui.selectedRelationId) {
      externalKeys.add(`relation:${ui.selectedRelationId}`);
    }
    // No-op-detectie: als local al equal is aan extern → niets doen
    // (anders triggert forward-push een eindeloze keten van re-renders).
    if (externalKeys.size === selectedRows.size) {
      let allMatch = true;
      for (const k of externalKeys) {
        if (!selectedRows.has(k)) {
          allMatch = false;
          break;
        }
      }
      if (allMatch) return;
    }
    // BEWUST `setSelectedRowsRaw` (geen wrapper): dit is een PULL uit
    // useUi naar local — als we hier de wrapper zouden gebruiken zou
    // de "no-op detectie" wel werken maar het feels-cleaner om de
    // intentie expliciet te maken. Geen push terug naar useUi nodig
    // want de externe state is per definitie al consistent.
    setSelectedRowsRaw(externalKeys);
    if (externalKeys.size > 0) {
      const firstKey = externalKeys.values().next().value as string | undefined;
      if (firstKey !== undefined) {
        setLastAnchor(firstKey);
        // Scroll de eerste geselecteerde rij in view — anders is een
        // canvas-rubber-band over far-apart-rows niet zichtbaar in
        // de tabel (selectie staat off-screen, modelleur denkt "er
        // is niets geselecteerd" terwijl counter wél klopt). cursorIdx
        // mee bijwerken zodat de bestaande scroll-into-view-useEffect
        // ook firet en alle keyboard-vervolgnav vanaf hier verder gaat.
        const firstIdx = rows.findIndex((r) => rowKey(r) === firstKey);
        if (firstIdx >= 0) setCursorIdx(firstIdx);
      }
    } else {
      setLastAnchor(null);
      setCursorIdx(-1);
    }
  }, [
    // We hebben deze props NIET nodig binnen 't effect (we lezen via
    // getState), maar wel als trigger zodat 't effect firet wanneer
    // de werkelijke external state wijzigt. De `void`-statements
    // hierboven onderdrukken Biome's "unused"-warning.
    uiSelectedConceptIds,
    uiSelectedRelationIds,
    selectedConceptId,
    selectedRelationId,
    selectedRows,
  ]);

  // (Voorheen was hier 'n unmount-cleanup die useUi.selectedConceptIds/
  // RelationIds leeg zette wanneer de modelleur naar tekst-view
  // switchte. Verwijderd om twee redenen:
  //   1. Multi-selectie is een CANVAS-niveau-concept, niet pane-specifiek
  //      — als je 5 objecten geselecteerd hebt en je sluit de tabel,
  //      hoort de canvas-highlight te blijven.
  //   2. React StrictMode-double-mount triggerde deze cleanup in dev
  //      direct bij mount, wat de externe state wipede die canvas net
  //      had gevuld → bij re-mount zag backward-sync alleen nog 1 rij
  //      (de single-select) i.p.v. 5.
  // De backward-sync verderop pakt sowieso elke externe state-wijziging
  // op; geen aparte cleanup nodig.)

  // Anti-spam: voorkomt dat een per-ongeluk dubbele DEL-toets dubbele
  // remove-calls firet (eerste loopt nog, tweede ziet stale state).
  const [deleting, setDeleting] = useState<boolean>(false);

  // Insert-row UI-state: dialog open/dicht + rechtermuis-context-menu.
  // Beide triggers vuren `handleInsertConcept` of `handleInsertRelation`.
  const [insertDialog, setInsertDialog] = useState<{ kind: "concept" | "relation" } | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; rowKey: string } | null>(
    null,
  );
  // Pending-move-state: rijen die de gebruiker heeft "geknipt" voor
  // verplaatsing via het context-menu. Bij Plakken hier op een doelrij
  // worden ze direct ÓNDER die rij geplaatst via tableOrder-renumber.
  // Esc wist 't (analoog aan systeem-knip-en-plak). Pending-rijen krijgen
  // tijdelijk een verlaagde opacity zodat duidelijk is welke rijen op
  // hun verplaatsing wachten.
  const [pendingMoveKeys, setPendingMoveKeys] = useState<Set<string> | null>(null);
  // Zoekfunctie — filter rijen op vrije-tekst-match (name, label,
  // sourceName, targetName, typeShort). Case-insensitive contains.
  // Update triggert rows-useMemo herrekenen via dep-list.
  const [searchQuery, setSearchQuery] = useState("");
  // Stereotype-edit-modal-state. Geopend door dubbelklik op kolom E
  // ("Stereotypes") van een rij. Bevat de actuele selectie als Set zodat
  // toggle goedkoop is; bij Opslaan patchen we het concept/relation met
  // de array-versie via patchConcept / patchRelation.
  const [stereoEdit, setStereoEdit] = useState<{
    anchorId: string;
    kind: "concept" | "relation";
    typeId: string;
    selected: Set<string>;
  } | null>(null);

  // Ref op de context-menu container — gebruikt door de window-capture-
  // mousedown-close-handler om te detecteren of de klik BINNEN 't menu
  // gebeurt (dan NIET sluiten — anders mist de menu-button-click).
  const contextMenuRef = useRef<HTMLDivElement | null>(null);

  // Globale Alt+N / Option+N — werkt vanaf elke focus-positie zolang
  // GridPane gemount is. Voorheen vereiste de handler dat de wrapper-
  // div focus had (tabIndex=0), wat betekende dat je éérst in de tabel
  // moest klikken voordat de shortcut werkte. Met deze window-level
  // listener werkt 'm direct ná het openen van de tabel-pane.
  //
  // Guards:
  //  - skipt als focus in een editeerbaar veld zit zodat typen van
  //    "n" met Option ingedrukt geen dialog opent;
  //  - skipt als de InsertRowDialog al open is (double-open
  //    voorkomen);
  //  - skipt als er geen actief diagram is (handlers verwachten 't).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (insertDialog) return;
      if (!activeDiagramId) return;
      if (useSession.getState().readOnly) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || target?.isContentEditable) {
        return;
      }
      if (
        e.altKey &&
        !e.metaKey &&
        !e.ctrlKey &&
        (e.code === "KeyN" || e.key.toLowerCase() === "n")
      ) {
        e.preventDefault();
        setInsertDialog({ kind: "concept" });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [insertDialog, activeDiagramId]);

  // Sluit context-menu bij klik buiten 't menu of Esc.
  useEffect(() => {
    if (!contextMenu) return;
    const onMouseDown = (e: MouseEvent) => {
      const menuEl = contextMenuRef.current;
      if (menuEl && e.target instanceof Node && menuEl.contains(e.target)) return;
      setContextMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setContextMenu(null);
    };
    window.addEventListener("mousedown", onMouseDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("mousedown", onMouseDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [contextMenu]);

  /**
   * Bereken een nieuwe `tableOrder` waarmee een net-aangemaakte rij
   * direct ná de cursor-rij (binnen z'n eigen kind-groep) verschijnt.
   * Volgt dezelfde "tableOrder ?? naturalIdx"-fallback-regel die `rows`
   * gebruikt — zie de useMemo hierboven.
   */
  const computeInsertTableOrder = (
    kind: "concept" | "relation",
    diagram: { viewState: Record<string, { tableOrder?: number }> },
  ): number => {
    const store = useWorkspaceData.getState().store;
    // Herbouw de gesorteerde-groep-volgorde uit de store — duplicaat van
    // de rows-useMemo maar zonder caching-overhead voor deze ene call.
    const inDiagram = new Set<string>();
    for (const [key, ve] of Object.entries(
      diagram.viewState as Record<
        string,
        { position?: unknown; hidden?: unknown; conceptRef?: string }
      >,
    )) {
      if ((ve as { position?: unknown }).position === undefined) continue;
      if ((ve as { hidden?: unknown }).hidden === true) continue;
      inDiagram.add(
        (ve as { ref?: string; conceptRef?: string }).ref ??
          (ve as { conceptRef?: string }).conceptRef ??
          key,
      );
    }
    // Multi-instance lookup voor relaties (idem hoofd-rendering): bouw
    // ref→ve-map zodat UUID-keyed entries ook gevonden worden.
    const relationVeByRefId2 = new Map<string, ViewElementState>();
    for (const [k, ve] of Object.entries(diagram.viewState as Record<string, ViewElementState>)) {
      if (ve.position !== undefined) continue;
      if (ve.type === "object") continue;
      const relId = ve.ref ?? k;
      if (!relationVeByRefId2.has(relId)) relationVeByRefId2.set(relId, ve);
    }
    const items =
      kind === "concept"
        ? store.listConcepts().filter((c) => !c.tombstoneInBranch && inDiagram.has(c.id))
        : store.listRelations().filter((r) => {
            if (r.tombstoneInBranch) return false;
            const ve = relationVeByRefId2.get(r.id);
            if (!ve) return false;
            if ((ve as { hidden?: unknown }).hidden === true) return false;
            return inDiagram.has(r.sourceConceptId) && inDiagram.has(r.targetConceptId);
          });
    const sorted = items
      .map((it, i) => ({
        id: it.id,
        i,
        o: diagram.viewState[it.id]?.tableOrder ?? i,
      }))
      .sort((a, b) => a.o - b.o || a.i - b.i);

    const cursorRow = cursorIdx >= 0 ? rows[cursorIdx] : null;
    const anchorId = cursorRow?.kind === kind ? cursorRow.anchorId : null;
    if (anchorId) {
      const idx = sorted.findIndex((x) => x.id === anchorId);
      if (idx >= 0 && idx < sorted.length - 1) {
        const before = sorted[idx]!.o;
        const after = sorted[idx + 1]!.o;
        return (before + after) / 2;
      }
    }
    // Geen anchor binnen deze kind-groep (cursor staat op andere kind of
    // op laatste rij), of anchor niet gevonden → ná laatste item komen.
    const last = sorted[sorted.length - 1];
    return last ? last.o + 1 : 0;
  };

  /**
   * Verplaats de huidige multi-select één rij omhoog (delta=-1) of omlaag
   * (delta=+1) binnen z'n eigen kind-groep (concepts of relations). Werkt
   * via tableOrder-renumber op de hele groep. Aanroep vanuit Cmd+Shift+↑/↓
   * keydown handler.
   *
   * Strategie per-rij i.p.v. blok: voor elke geselecteerde rij in de
   * juiste volgorde (descending bij down, ascending bij up) zoek de eerste
   * niet-geselecteerde naburige rij in de richting; swap die twee. Skipt
   * over andere geselecteerde rijen zodat losse-rij-multi-selecties als
   * onafhankelijke moves voelen (analoog aan VS Code's "Move Line"-met-
   * meerdere-cursors). Boundary-rijen (al bovenaan/onderaan) blijven
   * staan; de rest schuift.
   *
   * Na de move renumberen we ALLE rijen in de groep op opeenvolgende
   * integer-tableOrders — eenvoudig en deterministisch, geen drift van
   * fractionele midpoints over veel moves. De cursor verspringt mee naar
   * de nieuwe positie van de oorspronkelijke cursor-rij.
   */
  const moveSelectedRows = (delta: 1 | -1): void => {
    if (!workspaceId || !branchId || !activeDiagramId) return;
    if (useSession.getState().readOnly) return;
    if (selectedRows.size === 0) return;

    // Bepaal kind uit de eerste geselecteerde key (`concept:…` /
    // `relation:…`). We verplaatsen alleen binnen die kind-groep — een
    // mixed-selectie zou ofwel verlies aan informatie geven (relations
    // kunnen niet tussen concepts staan) ofwel rare gaps creëren.
    const firstKey = selectedRows.values().next().value as string | undefined;
    if (!firstKey) return;
    const kind: "concept" | "relation" = firstKey.startsWith("concept:") ? "concept" : "relation";

    const sameKindRows = rows.filter((r) => r.kind === kind);
    if (sameKindRows.length === 0) return;

    const selectedLocal = new Set<number>();
    sameKindRows.forEach((r, i) => {
      if (selectedRows.has(rowKey(r))) selectedLocal.add(i);
    });
    if (selectedLocal.size === 0) return;

    // Sorteer descending voor down (we shiften vanaf onderkant zodat
    // hogere indices niet wegvallen onder ons), ascending voor up.
    const sortedSel = [...selectedLocal].sort((a, b) => (delta === 1 ? b - a : a - b));
    const newOrder = [...sameKindRows];
    const movingSet = new Set(selectedLocal);

    for (const idx of sortedSel) {
      let j = idx + delta;
      while (j >= 0 && j < newOrder.length && movingSet.has(j)) {
        j += delta; // skip over andere geselecteerde rijen
      }
      if (j < 0 || j >= newOrder.length) continue; // boundary — laat staan
      // Swap positions
      const tmp = newOrder[idx];
      newOrder[idx] = newOrder[j]!;
      newOrder[j] = tmp!;
      // Track waar deze geselecteerde rij nu staat
      movingSet.delete(idx);
      movingSet.add(j);
    }

    // Renumber: zet tableOrder = nieuwe local index voor elke rij in deze
    // groep. De debounce in schedulePersistDiagram bundelt de N
    // setViewElementTableOrder-calls tot één patchDiagram-roundtrip.
    const ws = useWorkspaceData.getState();
    newOrder.forEach((row, i) => {
      ws.setViewElementTableOrder(workspaceId, branchId, row.anchorId, i);
    });

    // Cursor mee-schuiven: vind nieuwe local idx van cursor-row, convert
    // naar global rows-idx (concepts eerst, dan relations).
    if (cursorIdx >= 0) {
      const cursorRow = rows[cursorIdx];
      if (cursorRow && cursorRow.kind === kind) {
        const newLocalIdx = newOrder.findIndex((r) => r.anchorId === cursorRow.anchorId);
        if (newLocalIdx >= 0) {
          const numConcepts = rows.filter((r) => r.kind === "concept").length;
          const newGlobalIdx = kind === "concept" ? newLocalIdx : numConcepts + newLocalIdx;
          setCursorIdx(newGlobalIdx);
        }
      }
    }
  };

  /**
   * "Plakken hier" — context-menu-pendant van Cmd+Shift+Pijl. Verplaats
   * alle rijen in `pendingMoveKeys` (van dezelfde kind als targetRow) tot
   * direct ónder targetRow via tableOrder-renumber. Verstoort de kind-
   * volgorde niet — concept-pending kan alleen onder een concept-rij,
   * relation-pending onder een relation-rij.
   */
  const pasteMoveTo = (targetRowKey: string): void => {
    if (!pendingMoveKeys) return;
    if (!workspaceId || !branchId || !activeDiagramId) return;
    if (useSession.getState().readOnly) return;
    const targetRow = rows.find((r) => rowKey(r) === targetRowKey);
    if (!targetRow) return;
    // Filter pending tot dezelfde kind als de bestemming. Cross-kind paste
    // wordt stilzwijgend overgeslagen — voor de UX-MVP volstaat dat;
    // toekomstige versie kan beide kind-groepen apart paste'n.
    const kind = targetRow.kind;
    const sameKindRows = rows.filter((r) => r.kind === kind);
    const pendingInKind = sameKindRows.filter((r) => pendingMoveKeys.has(rowKey(r)));
    if (pendingInKind.length === 0) {
      setPendingMoveKeys(null);
      return;
    }
    // Bouw nieuwe volgorde: alle same-kind-rijen MIN pending, daarna
    // pending direct na de target-positie in de overgebleven lijst.
    const withoutPending = sameKindRows.filter((r) => !pendingMoveKeys.has(rowKey(r)));
    const targetIdx = withoutPending.findIndex((r) => rowKey(r) === targetRowKey);
    if (targetIdx < 0) {
      // Target was zelf in pending — onlogisch om "na zichzelf" te plakken.
      setPendingMoveKeys(null);
      return;
    }
    const newOrder = [
      ...withoutPending.slice(0, targetIdx + 1),
      ...pendingInKind,
      ...withoutPending.slice(targetIdx + 1),
    ];
    const ws = useWorkspaceData.getState();
    newOrder.forEach((row, i) => {
      ws.setViewElementTableOrder(workspaceId, branchId, row.anchorId, i);
    });
    setPendingMoveKeys(null);
  };

  /** Insert-concept-pad: smart-placement + addConceptOnCanvas + tableOrder. */
  const handleInsertConcept = async (typeId: string, name: string, stereotypes?: string[]) => {
    if (!workspaceId || !branchId || !activeDiagramId) return;
    if (useSession.getState().readOnly) return;
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return;
    const newTableOrder = computeInsertTableOrder("concept", diagram);
    const rects = [...buildAbsoluteRects(diagram.viewState).values()];
    // Anchor-canvas-positie: de cursor-rij's canvas-positie (als 't een
    // concept-rij is). findEmptyPlacement probeert dan eerst rechts/onder/
    // links/boven van die positie, en valt pas terug op "rechts naast
    // álle nodes" wanneer alle vier kandidaten overlappen.
    const cursorRow = cursorIdx >= 0 ? rows[cursorIdx] : null;
    let anchor: { x: number; y: number; w: number; h: number } | undefined;
    if (cursorRow?.kind === "concept" && cursorRow.position) {
      const ve = diagram.viewState[cursorRow.anchorId];
      anchor = {
        x: cursorRow.position.x,
        y: cursorRow.position.y,
        w: ve?.width ?? NEW_NODE_DEFAULT_WIDTH,
        h: ve?.height ?? NEW_NODE_DEFAULT_HEIGHT,
      };
    }
    const pos = findEmptyPlacement(rects, NEW_NODE_DEFAULT_WIDTH, NEW_NODE_DEFAULT_HEIGHT, anchor);
    try {
      const persisted = await useWorkspaceData
        .getState()
        .addConceptOnCanvas(
          workspaceId,
          branchId,
          typeId,
          { nl: name, en: name },
          pos,
          undefined,
          stereotypes,
        );
      useWorkspaceData
        .getState()
        .setViewElementTableOrder(workspaceId, branchId, persisted.id, newTableOrder);
      // Selecteer de nieuwe rij — backward-sync useEffect zet
      // selectedRows + cursorIdx + scroll-into-view.
      selectConcept(persisted.id);
    } catch {
      // addConceptOnCanvas heeft lastError al gezet
    }
  };

  /** Insert-relation-pad: connectConcepts + tableOrder + (optioneel) label-patch. */
  const handleInsertRelation = async (
    sourceConceptId: string,
    targetConceptId: string,
    relationTypeId: string,
    label: string,
  ) => {
    if (!workspaceId || !branchId || !activeDiagramId) return;
    if (useSession.getState().readOnly) return;
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return;
    const newTableOrder = computeInsertTableOrder("relation", diagram);
    try {
      const relation = await useWorkspaceData
        .getState()
        .connectConcepts(workspaceId, branchId, sourceConceptId, targetConceptId, relationTypeId);
      useWorkspaceData
        .getState()
        .setViewElementTableOrder(workspaceId, branchId, relation.id, newTableOrder);
      const trimmed = label.trim();
      if (trimmed) {
        await useWorkspaceData.getState().patchRelation(workspaceId, branchId, relation.id, {
          label: { nl: trimmed, en: trimmed },
        });
      }
      selectRelation(relation.id);
    } catch {
      // connectConcepts heeft lastError al gezet
    }
  };

  /**
   * Flex-relation-pad: source en/of target mag "nieuw concept" zijn —
   * dan maken we 'm eerst aan met addConceptOnCanvas, daarna pas de
   * relatie. Voor canvas-plaatsing van een nieuwe target nemen we de
   * source's positie als anker zodat de relatie visueel kort en
   * horizontaal blijft.
   */
  const handleInsertRelationFlex = async (
    source: RelationEndpoint,
    target: RelationEndpoint,
    relationTypeId: string,
    label: string,
  ): Promise<void> => {
    if (!workspaceId || !branchId || !activeDiagramId) return;
    if (useSession.getState().readOnly) return;
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return;
    const ws = useWorkspaceData.getState();
    let sourceId: string;
    let targetId: string;

    // ── Bron-pad ──
    if (source.kind === "existing") {
      sourceId = source.id;
    } else {
      const rects = [...buildAbsoluteRects(diagram.viewState).values()];
      const cursorRow = cursorIdx >= 0 ? rows[cursorIdx] : null;
      let anchor: { x: number; y: number; w: number; h: number } | undefined;
      if (cursorRow?.kind === "concept" && cursorRow.position) {
        const ve = diagram.viewState[cursorRow.anchorId];
        anchor = {
          x: cursorRow.position.x,
          y: cursorRow.position.y,
          w: ve?.width ?? NEW_NODE_DEFAULT_WIDTH,
          h: ve?.height ?? NEW_NODE_DEFAULT_HEIGHT,
        };
      }
      const pos = findEmptyPlacement(
        rects,
        NEW_NODE_DEFAULT_WIDTH,
        NEW_NODE_DEFAULT_HEIGHT,
        anchor,
      );
      const created = await ws.addConceptOnCanvas(
        workspaceId,
        branchId,
        source.typeId,
        { nl: source.name, en: source.name },
        pos,
      );
      sourceId = created.id;
    }

    // ── Doel-pad ──
    if (target.kind === "existing") {
      targetId = target.id;
    } else {
      // Anker = source-positie (bestaand of net aangemaakt) zodat de
      // relatie visueel een korte horizontale lijn wordt.
      const freshDiagram = useWorkspaceData.getState().store.getDiagram(activeDiagramId);
      const sourceVe = freshDiagram?.viewState[sourceId];
      const rects = [...buildAbsoluteRects(freshDiagram?.viewState ?? {}).values()];
      let anchor: { x: number; y: number; w: number; h: number } | undefined;
      if (sourceVe?.position) {
        anchor = {
          x: sourceVe.position.x,
          y: sourceVe.position.y,
          w: sourceVe.width ?? NEW_NODE_DEFAULT_WIDTH,
          h: sourceVe.height ?? NEW_NODE_DEFAULT_HEIGHT,
        };
      }
      const pos = findEmptyPlacement(
        rects,
        NEW_NODE_DEFAULT_WIDTH,
        NEW_NODE_DEFAULT_HEIGHT,
        anchor,
      );
      const created = await ws.addConceptOnCanvas(
        workspaceId,
        branchId,
        target.typeId,
        { nl: target.name, en: target.name },
        pos,
      );
      targetId = created.id;
    }

    // ── Relatie ──
    const newTableOrder = computeInsertTableOrder("relation", diagram);
    const relation = await useWorkspaceData
      .getState()
      .connectConcepts(workspaceId, branchId, sourceId, targetId, relationTypeId);
    useWorkspaceData
      .getState()
      .setViewElementTableOrder(workspaceId, branchId, relation.id, newTableOrder);
    const trimmed = label.trim();
    if (trimmed) {
      await useWorkspaceData.getState().patchRelation(workspaceId, branchId, relation.id, {
        label: { nl: trimmed, en: trimmed },
      });
    }
    selectRelation(relation.id);
  };

  /**
   * PlantUML-paste-pad: parse de meegegeven snippet via de bestaande
   * `parseArchiMatePlantUML` + `applyParseResult`-pipeline (zelfde als de
   * Onderwater-venster-PlantUML-modus gebruikt). Werkt additief — concepts
   * en relations die NIET in de snippet staan worden niet weggegooid (geen
   * scope-restrictie). Bij parse-errors gooit deze handler een Error die
   * de dialog netjes toont; bij succes komt de ApplyDiff terug.
   */
  const handleImportPlantUml = async (text: string) => {
    if (!workspaceId || !branchId || !activeDiagramId) {
      throw new Error(t("gridPane.noActiveDiagram"));
    }
    if (useSession.getState().readOnly) throw new Error(t("app.header.readOnly"));
    const result = parseArchiMatePlantUML(text);
    if (result.errors && result.errors.length > 0) {
      throw new Error(
        t("gridPane.parseErrors", {
          count: result.errors.length,
          errors: result.errors
            .slice(0, 3)
            .map((er) => er.message)
            .join("; "),
        }),
      );
    }
    const state = useWorkspaceData.getState();
    const diff = await applyParseResult(state, workspaceId, branchId, result, lang);
    return diff;
  };

  /**
   * Welke cel staat in edit-modus + tijdelijke buffer-waarde tot commit.
   * `null` = geen cel actief. Discriminated union per `field`:
   *
   *  - `name`         → kolom B; concept-naam of relatie-label (tekst-input)
   *  - `position`     → kolom C op concept; vrije "x, y"-tekst (parse op commit)
   *  - `sourceHandle` → kolom C op relatie; select-dropdown 4 zijden + auto
   *  - `targetHandle` → kolom D op relatie; idem
   */
  type EditingState =
    | { anchorId: string; field: "name"; buffer: string }
    | { anchorId: string; field: "position"; buffer: string }
    | { anchorId: string; field: "sourceHandle"; buffer: string }
    | { anchorId: string; field: "targetHandle"; buffer: string };
  const [editing, setEditing] = useState<EditingState | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Focus + select-all alleen wanneer een NIEUWE edit-sessie start —
  // niet bij elke keystroke binnen dezelfde sessie. Anders zou de
  // useEffect bij elke buffer-update opnieuw firen (editing is dan een
  // nieuw object via `setEditing({...editing, buffer: v})`), waardoor
  // `select()` de volledige tekst opnieuw selecteert en de volgende
  // toets de inhoud vervangt i.p.v. op cursor-positie te wissen.
  //
  // Identity-key = `<anchorId>:<field>`. Pas wanneer die wisselt (=
  // andere cel of ander veld) gebeurt 't focus+select-ritueel opnieuw.
  const editSessionKey = editing ? `${editing.anchorId}:${editing.field}` : null;
  useEffect(() => {
    if (editSessionKey && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editSessionKey]);

  const rows = useMemo<GridRow[]>(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    if (!activeDiagramId) return [];
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return [];

    const conceptIdSet = new Set<string>();
    for (const [key, ve] of Object.entries(diagram.viewState)) {
      if (ve.position === undefined) continue;
      if (ve.hidden === true) continue;
      conceptIdSet.add(ve.ref ?? key);
    }
    const conceptsNatural = store
      .listConcepts()
      .filter((c) => !c.tombstoneInBranch && conceptIdSet.has(c.id));
    const liveConceptIds = new Set(conceptsNatural.map((c) => c.id));

    // Multi-instance lookup: relatie kan via canonical key (= r.id) of
    // via UUID-keyed entry met ref=r.id zichtbaar zijn op dit diagram.
    // Bouw een ref-lookup-map ÉÉN keer voor performance.
    const relationVeByRefId = new Map<string, ViewElementState>();
    for (const [key, ve] of Object.entries(diagram.viewState)) {
      if (ve.position !== undefined) continue; // object-entry
      if (ve.type === "object") continue;
      const relId = ve.ref ?? key;
      // Eerste-wint per relation-id (Grid toont één rij per relation,
      // niet per instance).
      if (!relationVeByRefId.has(relId)) relationVeByRefId.set(relId, ve);
    }
    const relationsNatural = store.listRelations().filter((r) => {
      if (r.tombstoneInBranch) return false;
      if (!liveConceptIds.has(r.sourceConceptId)) return false;
      if (!liveConceptIds.has(r.targetConceptId)) return false;
      const ve = relationVeByRefId.get(r.id);
      if (!ve) return false;
      if (ve.hidden === true) return false;
      return true;
    });

    // Sorteer binnen elk van de twee groepen (concepts, relations) op
    // viewState.tableOrder met natuurlijke index als fallback. Items
    // zonder tableOrder vallen dus terug op hun listConcepts/-Relations-
    // positie en blijven onder elkaar staan. Een nieuw item met tableOrder
    // tussen twee fallback-indices schuift dáár tussen — dat is hoe
    // "Regel invoegen" een rij ná de cursor laat verschijnen zonder dat
    // bestaande rijen verschoven worden in de DB.
    const orderOf = (key: string, naturalIdx: number): number => {
      const ve = diagram.viewState[key];
      return ve?.tableOrder !== undefined ? ve.tableOrder : naturalIdx;
    };
    const concepts = conceptsNatural
      .map((c, i) => ({ c, i, o: orderOf(c.id, i) }))
      .sort((a, b) => a.o - b.o || a.i - b.i)
      .map((x) => x.c);
    const relations = relationsNatural
      .map((r, i) => ({ r, i, o: orderOf(r.id, i) }))
      .sort((a, b) => a.o - b.o || a.i - b.i)
      .map((x) => x.r);

    const out: GridRow[] = [];
    for (const c of concepts) {
      const ve = diagram.viewState[c.id];
      out.push({
        kind: "concept",
        anchorId: c.id,
        typeShort: shortTypeId(c.typeId),
        typeId: c.typeId,
        name: displayConceptName(c, lang),
        label: "",
        sourceName: "",
        targetName: "",
        position: ve?.position ?? null,
        stereotypes: c.stereotypes ?? [],
      });
    }
    // Absolute rectangles voor geometrische handle-benadering. Eén keer
    // gebouwd, hergebruikt voor elke relatie.
    const absRects = buildAbsoluteRects(diagram.viewState);
    for (const r of relations) {
      const ve = diagram.viewState[r.id];
      const source = store.getConcept(r.sourceConceptId);
      const target = store.getConcept(r.targetConceptId);
      // Voor handle-detectie: respecteer instance-keys (= viewState-key
      // is anders dan concept-id wanneer 't een extra plaatsing is).
      // Voor 't merendeel concepten is 't concept-id = viewState-key.
      const sourceKey = ve?.sourceInstanceId ?? r.sourceConceptId;
      const targetKey = ve?.targetInstanceId ?? r.targetConceptId;
      const approx = approxHandlesFromGeometry(absRects.get(sourceKey), absRects.get(targetKey));
      out.push({
        kind: "relation",
        anchorId: r.id,
        typeShort: shortTypeId(r.typeId),
        typeId: r.typeId,
        name: "",
        label: r.label ? resolveBilingual(r.label, lang) : "",
        sourceName: source ? displayConceptName(source, lang) : "?",
        targetName: target ? displayConceptName(target, lang) : "?",
        sourceAttach: formatAttachment(
          ve?.sourceHandle,
          ve?.sourceAngle,
          approx?.sourceHandle ?? null,
        ),
        targetAttach: formatAttachment(
          ve?.targetHandle,
          ve?.targetAngle,
          approx?.targetHandle ?? null,
        ),
        stereotypes: r.stereotypes ?? [],
      });
    }
    // Zoekfilter (case-insensitive contains over alle tekst-velden).
    // Lege query → toon alles. Filtering gebeurt NA sort zodat
    // tableOrder-volgorde behouden blijft binnen de filtered-set.
    const q = searchQuery.trim().toLowerCase();
    if (q === "") return out;
    return out.filter((row) => {
      const haystack = [row.name, row.label, row.sourceName, row.targetName, row.typeShort]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [revision, activeDiagramId, lang, searchQuery]);

  const handleRowClick = (row: GridRow, event: React.MouseEvent): void => {
    const key = rowKey(row);
    // Sync keyboard-cursor naar de geklikte rij zodat vervolg-pijl-
    // toetsen vanaf hier verder navigeren.
    const clickedIdx = rows.findIndex((r) => rowKey(r) === key);
    if (clickedIdx >= 0) setCursorIdx(clickedIdx);

    if (event.shiftKey && lastAnchor) {
      const idxA = rows.findIndex((r) => rowKey(r) === lastAnchor);
      const idxB = clickedIdx;
      if (idxA >= 0 && idxB >= 0) {
        const [lo, hi] = idxA < idxB ? [idxA, idxB] : [idxB, idxA];
        const next = new Set<string>();
        for (let i = lo; i <= hi; i++) {
          const r = rows[i];
          if (r) next.add(rowKey(r));
        }
        setSelectedRows(next);
      }
      return;
    }
    if (event.metaKey || event.ctrlKey) {
      const next = new Set(selectedRows);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      setSelectedRows(next);
      setLastAnchor(key);
      return;
    }
    setSelectedRows(new Set([key]));
    setLastAnchor(key);
    if (row.kind === "concept") selectConcept(row.anchorId);
    else selectRelation(row.anchorId);
  };

  /**
   * Delete-pad voor geselecteerde rijen — zelfde semantiek als het canvas:
   *
   *  - `hard = false` (DEL/Backspace): van DIT diagram verwijderen. Het
   *    object blijft in het model en op andere diagrammen staan. Alle
   *    instances van de rij op dit diagram gaan weg (een concept kan er
   *    meerdere keren staan). Eén undo-entry voor de hele selectie.
   *  - `hard = true` (Cmd/Ctrl/Alt+DEL, of "Uit model verwijderen…" in het
   *    menu): na de cascade-bevestiging (`confirmHardDelete`, dezelfde als
   *    op het canvas) uit het model verwijderen.
   *
   * Hard-delete-volgorde (in `removeBatch`): relaties waarvan een endpoint
   * óók in de batch zit, gaan mee met `removeConcept` (die neemt ze in z'n
   * undo-snapshot mee); de overige relaties worden expliciet verwijderd.
   * Batched met één gecombineerde undo-entry — per-concept-undo's kunnen
   * een relatie niet restoren als het andere endpoint inmiddels een nieuwe
   * id heeft.
   */
  const handleDeleteSelected = async (hard: boolean): Promise<void> => {
    if (deleting) return;
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    if (selectedRows.size === 0) return;
    setDeleting(true);
    try {
      const ws = useWorkspaceData.getState();
      const toRemove = rows.filter((r) => selectedRows.has(rowKey(r)));
      const conceptIds = toRemove.filter((r) => r.kind === "concept").map((r) => r.anchorId);
      const relationIds = toRemove.filter((r) => r.kind === "relation").map((r) => r.anchorId);
      if (hard) {
        const conceptSet = new Set(conceptIds);
        // Relaties met een endpoint in de batch verdwijnen al via de
        // cascade — die telt de bevestiging apart.
        const standalone = relationIds.filter((id) => {
          const rel = ws.store.getRelation(id);
          return (
            !rel || (!conceptSet.has(rel.sourceConceptId) && !conceptSet.has(rel.targetConceptId))
          );
        });
        const ok = await confirmHardDelete({
          workspaceId,
          branchId,
          conceptIds,
          lang,
          standaloneEdgeCount: standalone.length,
          conceptName: (id) => ws.store.getConcept(id)?.name,
        });
        if (!ok) return;
        await ws.removeBatch(workspaceId, branchId, conceptIds, relationIds);
      } else {
        const diagram = activeDiagramId ? ws.store.getDiagram(activeDiagramId) : undefined;
        if (!diagram) return;
        const { conceptKeys, relationKeys } = diagramEntryKeysForRows(
          diagram.viewState,
          conceptIds,
          relationIds,
        );
        // Eerst de relaties, dan de concepten: hideConceptOnDiagram neemt
        // afhankelijke relatie-instances zelf mee, en een relatie die al weg
        // is zou in hideRelationOnDiagram een `hidden`-marker krijgen.
        ws.beginUserUndoBatch();
        try {
          for (const key of relationKeys) ws.hideRelationOnDiagram(workspaceId, branchId, key);
          for (const key of conceptKeys) ws.hideConceptOnDiagram(workspaceId, branchId, key);
        } finally {
          ws.endUserUndoBatch("Verwijder rijen van diagram");
        }
      }
      setSelectedRows(new Set());
      setLastAnchor(null);
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Dubbel-klik op de tekstuele cel: open edit-modus met het bewerkbare
   * deel (concept-naam óf relatie-label) als buffer. De click-event-
   * propagatie naar de rij-select-handler wordt gestopt zodat de huidige
   * selectie blijft staan.
   *
   * Belangrijk: buffer initt op de RAW concept.name in de huidige taal,
   * niet op de gerenderde row.name. Anders zou een junction (waar
   * row.name een `<en>`/`<and>`-placeholder is) bij commit die
   * placeholder als echte naam opslaan — ongewenst.
   */
  const handleCellDoubleClick = (row: GridRow, event: React.MouseEvent): void => {
    event.stopPropagation();
    if (useSession.getState().readOnly) return;
    const ws = useWorkspaceData.getState();
    let initial = "";
    if (row.kind === "concept") {
      const c = ws.store.getConcept(row.anchorId);
      initial = c ? (c.name[lang] ?? "") : "";
    } else {
      initial = row.label;
    }
    setEditing({ anchorId: row.anchorId, field: "name", buffer: initial });
  };

  /**
   * Commit-pad: schrijf de buffer naar de store. Per field-discriminant
   * een ander pad: name → patchConcept/patchRelation, position →
   * moveConceptOnCanvas (na parse), sourceHandle/targetHandle →
   * setRelationHandlesOnCanvas (per kant, andere kant blijft staan).
   */
  const commitEdit = async (): Promise<void> => {
    if (!editing || !workspaceId || !branchId || useSession.getState().readOnly) {
      setEditing(null);
      return;
    }
    const ws = useWorkspaceData.getState();
    const row = rows.find((r) => r.anchorId === editing.anchorId);
    if (!row) {
      setEditing(null);
      return;
    }
    const buf = editing.buffer.trim();

    if (editing.field === "name") {
      if (row.kind === "concept") {
        const existing = ws.store.getConcept(row.anchorId);
        if (!existing) {
          setEditing(null);
          return;
        }
        const currentLang = existing.name[lang] ?? "";
        if (currentLang === buf) {
          setEditing(null);
          return;
        }
        const nextName = { ...existing.name, [lang]: buf };
        try {
          await ws.patchConcept(workspaceId, branchId, row.anchorId, { name: nextName });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`patchConcept(${row.anchorId}, name) failed`, err);
        }
      } else {
        const existing = ws.store.getRelation(row.anchorId);
        if (!existing) {
          setEditing(null);
          return;
        }
        const currentLang = existing.label?.[lang] ?? "";
        if (currentLang === buf) {
          setEditing(null);
          return;
        }
        const nextLabel = { ...(existing.label ?? {}), [lang]: buf };
        try {
          await ws.patchRelation(workspaceId, branchId, row.anchorId, { label: nextLabel });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`patchRelation(${row.anchorId}, label) failed`, err);
        }
      }
    } else if (editing.field === "position") {
      // Parse "x, y" (komma of spatie als separator, beide toegestaan).
      const parts = buf.split(/[, ]+/).filter((s) => s.length > 0);
      if (parts.length !== 2) {
        // eslint-disable-next-line no-console
        console.warn(`Position-input "${buf}" niet als "x, y" leesbaar; commit geannuleerd`);
        setEditing(null);
        return;
      }
      const x = Number(parts[0]);
      const y = Number(parts[1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        // eslint-disable-next-line no-console
        console.warn(`Position-input "${buf}" bevat geen geldige getallen; commit geannuleerd`);
        setEditing(null);
        return;
      }
      try {
        await ws.moveConceptOnCanvas(workspaceId, branchId, row.anchorId, { x, y });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`moveConceptOnCanvas(${row.anchorId}) failed`, err);
      }
    }
    setEditing(null);
  };

  /**
   * Direct-commit pad voor handle-dropdowns. De select-on-change-flow
   * mag niet via `editing.buffer`-state lopen omdat React's re-render
   * pas NA `setEditing` plaatsvindt — een achteraf-aangeroepen
   * `commitEdit` (via queueMicrotask of useEffect) leest dan nog de
   * vorige closure-buffer. In plaats daarvan committen we hier de
   * doorgekregen waarde rechtstreeks naar de store.
   */
  const commitHandleEdit = (
    anchorId: string,
    field: "sourceHandle" | "targetHandle",
    value: string,
  ): void => {
    if (!workspaceId || !branchId || !activeDiagramId || useSession.getState().readOnly) {
      setEditing(null);
      return;
    }
    const ws = useWorkspaceData.getState();
    const relation = ws.store.getRelation(anchorId);
    if (!relation) {
      setEditing(null);
      return;
    }
    const existing: ViewElementState = ws.store.getDiagram(activeDiagramId)?.viewState[
      anchorId
    ] ?? {
      type: "relation",
      ref: anchorId,
    };
    // `auto` = wis de expliciete hoek, andere zijde blijft staan.
    // We zetten BEWUST geen `sourceHandle`/`targetHandle` in de viewState
    // vanuit dit pad: de hoek alleen is genoeg voor RoutedEdge's
    // `pointFromAngle`-routing, en de handle-id daarnaast leidt tot
    // visuele pixel-mismatch (React Flow's handle-coords vs. onze
    // border-coords). Tabel-weergave herleidt de handle-naam terug uit
    // de canonieke hoek (zie `angleToCanonicalHandle`).
    const nextSourceAngle =
      field === "sourceHandle"
        ? value === "auto" || value === ""
          ? null
          : handleIdToAngle(value)
        : (existing.sourceAngle ?? null);
    const nextTargetAngle =
      field === "targetHandle"
        ? value === "auto" || value === ""
          ? null
          : handleIdToAngle(value)
        : (existing.targetAngle ?? null);
    try {
      ws.setRelationAnglesOnCanvas(
        workspaceId,
        branchId,
        anchorId,
        nextSourceAngle,
        nextTargetAngle,
      );
      // Ruim oud-opgeslagen handle-id's op zodat ze geen verwarrende
      // dubbele-bron-of-waarheid creëren op latere renders. Als de
      // gebruiker eerder via canvas-drag een handle had gezet (de
      // canvas-flow zet zowel handle als angle), laten we de handle
      // staan tot 't via deze tabel-edit weer gewist wordt.
      if (existing.sourceHandle || existing.targetHandle) {
        ws.setRelationHandlesOnCanvas(workspaceId, branchId, anchorId, null, null);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`setRelationAnglesOnCanvas(${anchorId}) failed`, err);
    }
    setEditing(null);
  };

  const cancelEdit = (): void => {
    setEditing(null);
  };

  /**
   * Open positie-edit (concept) of handle-edit (relation) op kolom C.
   * Voor concepten initieert 't met "x, y" als tekstbuffer; voor
   * relaties met de huidige sourceHandle (of "auto" bij geen).
   */
  const handleColCDoubleClick = (row: GridRow, event: React.MouseEvent): void => {
    event.stopPropagation();
    if (useSession.getState().readOnly) return;
    if (row.kind === "concept") {
      if (!row.position) {
        // Concept heeft geen positie (zou niet in tabel mogen staan, maar
        // defensief): open lege buffer zodat modelleur een verse waarde
        // kan invoeren.
        setEditing({ anchorId: row.anchorId, field: "position", buffer: "0, 0" });
        return;
      }
      setEditing({
        anchorId: row.anchorId,
        field: "position",
        buffer: `${Math.round(row.position.x)}, ${Math.round(row.position.y)}`,
      });
    } else {
      const ws = useWorkspaceData.getState();
      const ve = ws.store.getDiagram(activeDiagramId ?? "")?.viewState[row.anchorId];
      // Initial-buffer: prefer explicit handle, anders canonieke hoek
      // herleiden, anders "auto". Past bij wat formatAttachment toont.
      const initial =
        ve?.sourceHandle ??
        (typeof ve?.sourceAngle === "number"
          ? (angleToCanonicalHandle(ve.sourceAngle) ?? "auto")
          : "auto");
      setEditing({ anchorId: row.anchorId, field: "sourceHandle", buffer: initial });
    }
  };

  /** Open target-handle-edit (alleen op relatie-rijen). */
  const handleColDDoubleClick = (row: GridRow, event: React.MouseEvent): void => {
    event.stopPropagation();
    if (useSession.getState().readOnly) return;
    if (row.kind !== "relation") return;
    const ws = useWorkspaceData.getState();
    const ve = ws.store.getDiagram(activeDiagramId ?? "")?.viewState[row.anchorId];
    const initial =
      ve?.targetHandle ??
      (typeof ve?.targetAngle === "number"
        ? (angleToCanonicalHandle(ve.targetAngle) ?? "auto")
        : "auto");
    setEditing({ anchorId: row.anchorId, field: "targetHandle", buffer: initial });
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: grid-container-plumbing, geen semantische control; key-handler is bestaand tabel-niveau-gedrag
    <div
      data-scope="text-pane"
      className="flex h-full min-h-0 flex-col bg-(--ld-surface)"
      role="presentation"
      // Tabel-niveau key-handler: DEL/Backspace verwijdert geselecteerde
      // rijen. We luisteren op de container (tabIndex=0 nodig om focus
      // te kunnen ontvangen), en filteren keystrokes die uit input/select-
      // elementen komen (= cell-edit-modus) zodat normaal typen daar
      // niet per ongeluk rijen verwijdert.
      // biome-ignore lint/a11y/noNoninteractiveTabindex: bewust keyboard-focusbaar grid-oppervlak; tabIndex hoort bij het bestaande tabel-toetsbesturing-gedrag
      tabIndex={0}
      onKeyDown={(e) => {
        const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
        // Skip alle key-handlers wanneer 'n cell-edit (input/select/
        // textarea) focus heeft — anders zou Pijl-Down tijdens 't
        // bewerken van 'n naam-cel onverwacht de cursor verplaatsen.
        if (tag === "input" || tag === "select" || tag === "textarea") return;

        // ── DEL/Backspace ── geselecteerde rijen van het diagram halen;
        // mét Cmd/Ctrl/Alt uit het model (na bevestiging) — gelijk aan
        // het canvas.
        if (e.key === "Delete" || e.key === "Backspace") {
          if (selectedRows.size === 0) return;
          e.preventDefault();
          void handleDeleteSelected(e.metaKey || e.ctrlKey || e.altKey);
          return;
        }

        // ── Alt+N / Option+N ── insert-dialog openen (Cmd+N wordt door
        // Chrome bezet voor "Nieuw venster" — niet onderschepbaar vanuit
        // JS). Op macOS is Option+N de dead-key voor `~` (om `ñ` te
        // typen), dus `e.key` is dan "Dead" / "˜" — never "n". Pak
        // daarom `e.code === "KeyN"` (layout-agnostic, niet beïnvloed
        // door dead-keys), met `e.key`-fallback voor edge-browsers die
        // geen `code` zetten.
        if (
          e.altKey &&
          !e.metaKey &&
          !e.ctrlKey &&
          (e.code === "KeyN" || e.key.toLowerCase() === "n")
        ) {
          e.preventDefault();
          setInsertDialog({ kind: "concept" });
          return;
        }

        // ── Escape ── eerst pending-move opheffen (analoog aan systeem-
        // knip-en-plak waar Esc de klembord-indicator wist), daarna pas
        // de gewone "deselecteer alles".
        if (e.key === "Escape") {
          e.preventDefault();
          if (pendingMoveKeys) {
            setPendingMoveKeys(null);
            return;
          }
          setSelectedRows(new Set());
          setLastAnchor(null);
          setCursorIdx(-1);
          return;
        }

        // ── Space ── toggle selectie van huidige cursor-rij
        if (e.key === " ") {
          if (cursorIdx < 0 || cursorIdx >= rows.length) return;
          e.preventDefault();
          const row = rows[cursorIdx];
          if (!row) return;
          const key = rowKey(row);
          const next = new Set(selectedRows);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          setSelectedRows(next);
          setLastAnchor(key);
          return;
        }

        // ── Cmd+Shift+Pijl ↑/↓ ── verplaats geselecteerde rijen op/neer
        // binnen hun eigen kind-groep. MOET vóór de gewone ArrowUp/Down-
        // handler staan want anders pakt 't Shift+Pijl-pad de boel als
        // range-select. Werkt voor zowel single als multi-selectie;
        // boundary-rijen (al bovenaan/onderaan groep) blijven staan en
        // de rest schuift mee.
        if (
          (e.metaKey || e.ctrlKey) &&
          e.shiftKey &&
          (e.key === "ArrowDown" || e.key === "ArrowUp")
        ) {
          if (selectedRows.size === 0) return;
          e.preventDefault();
          moveSelectedRows(e.key === "ArrowDown" ? 1 : -1);
          return;
        }

        // ── Pijl ↑/↓ ── cursor verplaatsen + selectie-update
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          if (rows.length === 0) return;
          e.preventDefault();
          const delta = e.key === "ArrowDown" ? 1 : -1;
          const startIdx = cursorIdx < 0 ? (delta > 0 ? 0 : rows.length - 1) : cursorIdx;
          const nextIdx =
            cursorIdx < 0 ? startIdx : Math.max(0, Math.min(rows.length - 1, startIdx + delta));
          const row = rows[nextIdx];
          if (!row) return;
          const key = rowKey(row);

          if (e.shiftKey) {
            // Shift+Pijl: range van lastAnchor (of cursor-start) t/m
            // nieuwe cursor. Excel-stijl REPLACE — eerdere multi
            // buiten 't bereik vervalt.
            const anchorKey = lastAnchor ?? rowKey(rows[startIdx] ?? row);
            const idxA = rows.findIndex((r) => rowKey(r) === anchorKey);
            if (idxA >= 0) {
              const [lo, hi] = idxA < nextIdx ? [idxA, nextIdx] : [nextIdx, idxA];
              const next = new Set<string>();
              for (let i = lo; i <= hi; i++) {
                const r = rows[i];
                if (r) next.add(rowKey(r));
              }
              setSelectedRows(next);
            }
            setCursorIdx(nextIdx);
            return;
          }

          if (e.metaKey || e.ctrlKey) {
            // Cmd/Ctrl+Pijl: cursor verplaatsen ZONDER selectie aan
            // te raken (Excel-stijl: voorlopig naar 'n nieuwe cel
            // navigeren zonder de huidige selectie weg te gooien).
            setCursorIdx(nextIdx);
            return;
          }

          // Plain Pijl: cursor verplaatst + single-select op de nieuwe
          // rij. Wist eerdere multi-select (zoals een plain-klik in
          // de tabel zelf).
          setSelectedRows(new Set([key]));
          setLastAnchor(key);
          setCursorIdx(nextIdx);
          if (row.kind === "concept") selectConcept(row.anchorId);
          else selectRelation(row.anchorId);
          return;
        }

        // ── Home/End ── spring naar eerste/laatste rij
        if (e.key === "Home" || e.key === "End") {
          if (rows.length === 0) return;
          e.preventDefault();
          const nextIdx = e.key === "Home" ? 0 : rows.length - 1;
          const row = rows[nextIdx];
          if (!row) return;
          const key = rowKey(row);
          if (e.shiftKey && lastAnchor) {
            const idxA = rows.findIndex((r) => rowKey(r) === lastAnchor);
            if (idxA >= 0) {
              const [lo, hi] = idxA < nextIdx ? [idxA, nextIdx] : [nextIdx, idxA];
              const next = new Set<string>();
              for (let i = lo; i <= hi; i++) {
                const r = rows[i];
                if (r) next.add(rowKey(r));
              }
              setSelectedRows(next);
            }
          } else {
            setSelectedRows(new Set([key]));
            setLastAnchor(key);
            if (row.kind === "concept") selectConcept(row.anchorId);
            else selectRelation(row.anchorId);
          }
          setCursorIdx(nextIdx);
          return;
        }
      }}
    >
      {/* Header-rij: links titel + variant-context (komt vanuit Editor.tsx
          variant-selector), rechts de gedeelde RevealPaneHeaderControls
          (Mode-switcher / Preview / Copy / Pin / Verberg). copyText levert
          de geëmitte tekst van de actieve mode (per user-keuze 2026-05-24
          = de bron-data van de preview). */}
      <header className="flex shrink-0 items-center justify-between border-(--ld-border) border-b bg-(--ld-raised) px-3 py-2">
        <h2 className="font-semibold text-(--ld-text) text-sm">{t("textPane.title")}</h2>
        <RevealPaneHeaderControls copyText={reveal.currentText} />
      </header>
      <div className="flex shrink-0 items-center gap-2 border-(--ld-border) border-b bg-(--ld-raised) px-3 py-1 text-xs text-(--ld-muted)">
        <span>{t("gridPane.statusBar", { rows: rows.length, revision })}</span>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t("gridPane.searchPlaceholder")}
          aria-label={t("gridPane.searchAriaLabel")}
          className="w-44 rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-0.5 text-(--ld-text) text-xs normal-case focus:border-(--ld-accent) focus:outline-none"
        />
      </div>
      {/*
        Selectie-balk — toont alleen de teller + key-hint. Verwijderen /
        Deselecteren zitten nu in het rechter-muis-context-menu (en DEL
        respectievelijk Esc blijven werken via de container's key-handler).
        Klein en niet-actiegevend om visuele clutter te vermijden.
      */}
      {selectedRows.size > 0 && (
        <div className="flex shrink-0 items-center gap-2 border-(--ld-border) border-b bg-(--ld-active) px-3 py-1 text-xs">
          <span className="text-(--ld-text)">
            {t("gridPane.selectedCount", { n: selectedRows.size })}
          </span>
          <span className="ml-auto text-xs text-(--ld-muted)">
            {t("gridPane.selectionKeyHint")}
          </span>
        </div>
      )}
      {/* Tabel-area met optionele split: tabel links, live-preview rechts
          wanneer revealPreviewOpen. Bij preview=svg gebruiken we een
          read-only textarea (geen Kroki-roundtrip nodig); bij preview=
          plantuml/mermaid gebruiken we PreviewPane (Kroki / mermaid-js). */}
      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto">
          {/*
          Geen `w-full` op <table>: dan auto-fit de tabel z'n totale breedte
          aan de breedste inhoud i.p.v. de container te vullen. Kolommen C/D/E
          sluiten zo direct aan op kolom B in plaats van met witte ruimte
          tussen-in te zweven. Witruimte rechts van de tabel is bewust —
          't is de container die wijder is dan de inhoud, niet andersom.
        */}
          {/*
          `select-none` op de tabel: Shift+klik en Cmd+klik op rijen
          zouden anders ook de browser's native tekst-selectie uitbreiden,
          wat een tweede (blauwe) visuele highlight overlay over de
          rij-highlight legt. Modelleur kan tekst alsnog kopiëren via
          een dubbelklik op de cell-tekst (selecteert die cell-inhoud)
          of via cell-edit-mode (= input element waar selectie werkt).
        */}
          <table className="select-none border-collapse text-xs">
            <thead className="sticky top-0 bg-(--ld-active) text-(--ld-muted)">
              <tr>
                <th className="whitespace-nowrap border-(--ld-border) border-b px-2 py-1 text-left font-medium">
                  #
                </th>
                <th className="whitespace-nowrap border-(--ld-border) border-b px-2 py-1 text-left font-medium">
                  {t("gridPane.columns.b")}
                </th>
                <th className="whitespace-nowrap border-(--ld-border) border-b px-2 py-1 text-left font-medium">
                  {t("gridPane.columns.c")}
                </th>
                <th className="whitespace-nowrap border-(--ld-border) border-b px-2 py-1 text-left font-medium">
                  {t("gridPane.columns.d")}
                </th>
                <th className="whitespace-nowrap border-(--ld-border) border-b px-2 py-1 text-left font-medium">
                  {t("gridPane.columns.e")}
                </th>
                <th className="whitespace-nowrap border-(--ld-border) border-b px-2 py-1 text-left font-medium">
                  {t("gridPane.columns.f")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                // Selectie-status — uniform geel voor élke geselecteerde
                // rij. Twee bronnen tellen mee:
                //   1. Tabel-multi-set (selectedRows): wat de modelleur
                //      hier zelf heeft aangeklikt (incl. Cmd/Shift-multi)
                //   2. Canvas-single-select (useUi.selectedConceptId/
                //      RelationId): de modelleur klikte op canvas of in
                //      een ander paneel (Explorer, hover-tooltip-click)
                // De canvas spiegelt onze multi-set ook terug, dus binnen
                // dezelfde sessie convergeert 't visueel automatisch. Maar
                // bij een initial canvas-click (waar GridPane nog geen
                // local-row-state heeft) moeten we óók geel oplichten.
                const isSelected =
                  selectedRows.has(rowKey(row)) ||
                  (row.kind === "concept" && row.anchorId === selectedConceptId) ||
                  (row.kind === "relation" && row.anchorId === selectedRelationId);
                // Pending-move: rij wacht op "Plakken hier". Visualisatie =
                // verlaagde opacity zodat de gebruiker ziet welke rijen
                // straks verschuiven, zonder ze van de geel-selectie te
                // ontkoppelen.
                const isPendingMove = pendingMoveKeys?.has(rowKey(row)) ?? false;
                const editingThisRow = editing?.anchorId === row.anchorId ? editing : null;
                const isEditingName = editingThisRow?.field === "name";
                const isEditingPosition = editingThisRow?.field === "position";
                const isEditingSourceHandle = editingThisRow?.field === "sourceHandle";
                const isEditingTargetHandle = editingThisRow?.field === "targetHandle";
                const colCText =
                  row.kind === "concept"
                    ? row.position
                      ? `${Math.round(row.position.x)}, ${Math.round(row.position.y)}`
                      : "—"
                    : row.sourceAttach;
                const colDText = row.kind === "relation" ? row.targetAttach : "—";
                const anchor =
                  (row.kind === "concept" ? "concept:" : "relation:") + row.anchorId.slice(0, 8);
                const isCursor = idx === cursorIdx;
                return (
                  <tr
                    key={`${row.kind}-${row.anchorId}`}
                    ref={(el) => {
                      if (el) rowRefs.current.set(idx, el);
                      else rowRefs.current.delete(idx);
                    }}
                    onClick={(e) => handleRowClick(row, e)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      // Schuif de cursor mee naar de rechter-geklikte rij,
                      // zodat een vervolg-"Regel invoegen" deze rij als
                      // anker gebruikt en de nieuwe rij visueel ná de
                      // rechtsgeklikte rij landt. Zonder dit zou de cursor
                      // op een eerdere selectie blijven hangen en zou
                      // "Regel invoegen" naar 't einde van de groep gaan.
                      if (idx >= 0) setCursorIdx(idx);
                      setLastAnchor(rowKey(row));
                      setContextMenu({ x: e.clientX, y: e.clientY, rowKey: rowKey(row) });
                    }}
                    className={`cursor-pointer border-(--ld-rule) border-b hover:bg-(--ld-raised) ${
                      isSelected ? "bg-(--ld-notice-bg) hover:bg-(--ld-notice-bg)" : ""
                    } ${
                      // Toetsenbord-cursor: dunne blauwe rand-ring rondom
                      // de hele rij. Subtiel, valt niet met de selectie-
                      // achtergrond samen, en geeft toch duidelijk aan
                      // welke rij de "focus" heeft voor vervolg-Pijl-keys.
                      isCursor ? "outline outline-1 outline-(--ld-accent) -outline-offset-1" : ""
                    } ${
                      isPendingMove ? "opacity-60 italic" : ""
                    } ${row.kind === "relation" ? "text-(--ld-text)" : "text-(--ld-text)"}`}
                  >
                    <td className="whitespace-nowrap px-2 py-1 text-(--ld-muted) tabular-nums">
                      {idx + 1}
                    </td>
                    <td
                      className="whitespace-nowrap px-2 py-1 font-mono"
                      onDoubleClick={(e) => handleCellDoubleClick(row, e)}
                      title={
                        row.kind === "concept"
                          ? t("gridPane.titles.editName")
                          : t("gridPane.titles.editLabel")
                      }
                    >
                      {isEditingName ? (
                        <CellEditor
                          row={row}
                          buffer={editingThisRow.buffer}
                          onChange={(v) =>
                            setEditing(editingThisRow ? { ...editingThisRow, buffer: v } : null)
                          }
                          onCommit={commitEdit}
                          onCancel={cancelEdit}
                          inputRef={inputRef}
                        />
                      ) : (
                        renderRowText(row)
                      )}
                    </td>
                    <td
                      className="whitespace-nowrap px-2 py-1 font-mono text-(--ld-muted)"
                      onDoubleClick={(e) => handleColCDoubleClick(row, e)}
                      title={
                        row.kind === "concept"
                          ? t("gridPane.titles.editPosition")
                          : t("gridPane.titles.pickSourceHandle")
                      }
                    >
                      {isEditingPosition && row.kind === "concept" ? (
                        <PositionEditor
                          buffer={editingThisRow.buffer}
                          onChange={(v) =>
                            setEditing(
                              editingThisRow
                                ? { ...editingThisRow, field: "position", buffer: v }
                                : null,
                            )
                          }
                          onCommit={commitEdit}
                          onCancel={cancelEdit}
                          inputRef={inputRef}
                        />
                      ) : isEditingSourceHandle && row.kind === "relation" ? (
                        <HandleSelector
                          buffer={editingThisRow.buffer}
                          onChange={(v) => {
                            // Direct-commit met de doorgekregen waarde
                            // (zie `commitHandleEdit`-docstring); via
                            // `editing.buffer`-state zou de closure
                            // achteraf de oude waarde lezen.
                            commitHandleEdit(row.anchorId, "sourceHandle", v);
                          }}
                          onCancel={cancelEdit}
                        />
                      ) : (
                        colCText
                      )}
                    </td>
                    <td
                      className="whitespace-nowrap px-2 py-1 font-mono text-(--ld-muted)"
                      onDoubleClick={(e) => handleColDDoubleClick(row, e)}
                      title={
                        row.kind === "relation" ? t("gridPane.titles.pickTargetHandle") : undefined
                      }
                    >
                      {isEditingTargetHandle && row.kind === "relation" ? (
                        <HandleSelector
                          buffer={editingThisRow.buffer}
                          onChange={(v) => {
                            commitHandleEdit(row.anchorId, "targetHandle", v);
                          }}
                          onCancel={cancelEdit}
                        />
                      ) : (
                        colDText
                      )}
                    </td>
                    <td
                      className="max-w-[240px] truncate whitespace-nowrap px-2 py-1 text-(--ld-text)"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        if (readOnly) return;
                        setStereoEdit({
                          anchorId: row.anchorId,
                          kind: row.kind,
                          typeId: row.typeId,
                          selected: new Set(row.stereotypes),
                        });
                      }}
                      title={
                        row.stereotypes.length === 0
                          ? t("gridPane.titles.assignStereotypes")
                          : t("gridPane.titles.editStereotypes", { n: row.stereotypes.length })
                      }
                    >
                      {row.stereotypes.length === 0 ? (
                        <span className="text-(--ld-muted) italic">—</span>
                      ) : (
                        row.stereotypes
                          .map((sid) => {
                            const st = getRegistry().getStereotype(sid);
                            return st ? resolveBilingual(st.label, lang) : sid;
                          })
                          .join(", ")
                      )}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1 font-mono text-xs text-(--ld-muted)">
                      {anchor}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-(--ld-muted) italic">
                    {t("gridPane.noRows")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {revealPreviewOpen && (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col border-(--ld-border) border-l">
            <PreviewPane
              text={reveal.mode === "plantuml" ? reveal.plantumlText : reveal.mermaidText}
              notation={reveal.mode}
            />
          </div>
        )}
      </div>

      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="fixed z-[9999] min-w-[200px] rounded-md border border-(--ld-control) bg-(--ld-surface) py-1 shadow-[0_8px_24px_-4px_rgba(15,23,42,0.25),0_4px_8px_-2px_rgba(15,23,42,0.15)] ring-1 ring-(--ld-border)"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {/* Plakken hier — alleen relevant als er rijen op verplaatsing
              wachten, en alleen als de rechter-geklikte rij niet zelf in
              het pending-blok zit (anders zou je "na zichzelf" plakken). */}
          {pendingMoveKeys && !pendingMoveKeys.has(contextMenu.rowKey) && (
            <button
              type="button"
              className="w-full px-3 py-1.5 text-left text-sm font-medium text-(--ld-accent) hover:bg-(--ld-active)"
              onClick={() => {
                const target = contextMenu.rowKey;
                setContextMenu(null);
                pasteMoveTo(target);
              }}
            >
              {t("gridPane.menu.pasteHere", { n: pendingMoveKeys.size })}
            </button>
          )}
          {pendingMoveKeys && (
            <button
              type="button"
              className="w-full px-3 py-1.5 text-left text-sm text-(--ld-text) hover:bg-(--ld-raised)"
              onClick={() => {
                setContextMenu(null);
                setPendingMoveKeys(null);
              }}
            >
              {t("gridPane.menu.cancelMove")}
            </button>
          )}
          {pendingMoveKeys && <div className="my-1 border-(--ld-rule) border-b" />}

          <button
            type="button"
            className="w-full px-3 py-1.5 text-left text-sm text-(--ld-text) hover:bg-(--ld-raised)"
            hidden={readOnly}
            onClick={() => {
              setContextMenu(null);
              setInsertDialog({ kind: "concept" });
            }}
          >
            {t("gridPane.menu.insertRow")}
          </button>

          {/* Toon op canvas — viewport centreren op het object/relatie van
              de rechter-geklikte rij. Pakt anchorId uit de rowKey, vraagt
              useUi om de focus-request te zetten; Canvas listened daarop
              en roept ReactFlow's setCenter / fitView aan. */}
          <button
            type="button"
            className="w-full px-3 py-1.5 text-left text-sm text-(--ld-text) hover:bg-(--ld-raised)"
            onClick={() => {
              const ctxKey = contextMenu.rowKey;
              setContextMenu(null);
              // rowKey format = "concept:<uuid>" of "relation:<uuid>".
              const colonIdx = ctxKey.indexOf(":");
              const anchorId = colonIdx >= 0 ? ctxKey.slice(colonIdx + 1) : ctxKey;
              useUi.getState().requestViewportFocus(anchorId);
            }}
          >
            {t("gridPane.menu.showOnCanvas")}
          </button>

          {/* Verplaatsen — markeert de huidige selectie als "te
              verplaatsen". Vereist tenminste één geselecteerde rij. Als
              er nog niets geselecteerd is maar de gebruiker rechtsklikt
              op een rij, gebruik de rechts-geklikte rij als impliciete
              één-rij-selectie. */}
          {!pendingMoveKeys && (
            <button
              type="button"
              className="w-full px-3 py-1.5 text-left text-sm text-(--ld-text) hover:bg-(--ld-raised)"
              hidden={readOnly}
              onClick={() => {
                const ctxKey = contextMenu.rowKey;
                setContextMenu(null);
                if (selectedRows.size > 0) {
                  setPendingMoveKeys(new Set(selectedRows));
                } else {
                  setPendingMoveKeys(new Set([ctxKey]));
                }
              }}
            >
              {t("gridPane.menu.move")}
            </button>
          )}

          {selectedRows.size > 0 && !pendingMoveKeys && (
            <>
              <div className="my-1 border-(--ld-rule) border-b" />
              <button
                type="button"
                className="w-full px-3 py-1.5 text-left text-sm text-(--ld-negative) hover:bg-(--ld-negative-bg)"
                hidden={readOnly}
                onClick={() => {
                  setContextMenu(null);
                  void handleDeleteSelected(false);
                }}
              >
                {t("gridPane.menu.removeFromDiagram", { n: selectedRows.size })}
              </button>
              <button
                type="button"
                className="w-full px-3 py-1.5 text-left text-sm text-(--ld-negative) hover:bg-(--ld-negative-bg)"
                hidden={readOnly}
                onClick={() => {
                  setContextMenu(null);
                  void handleDeleteSelected(true);
                }}
              >
                {t("gridPane.menu.deleteFromModel", { n: selectedRows.size })}
              </button>
              <button
                type="button"
                className="w-full px-3 py-1.5 text-left text-sm text-(--ld-text) hover:bg-(--ld-raised)"
                onClick={() => {
                  setContextMenu(null);
                  setSelectedRows(new Set());
                  setLastAnchor(null);
                }}
              >
                {t("gridPane.menu.deselect")}
              </button>
            </>
          )}
        </div>
      )}

      <InsertRowDialog
        open={readOnly ? null : insertDialog}
        onClose={() => setInsertDialog(null)}
        onCreateConcept={handleInsertConcept}
        onCreateRelation={handleInsertRelation}
        onImportPlantUml={handleImportPlantUml}
        onCreateRelationFlex={handleInsertRelationFlex}
      />

      {stereoEdit && (
        <StereotypeEditModal
          editingState={stereoEdit}
          lang={lang}
          onClose={() => setStereoEdit(null)}
          onSave={async (selected) => {
            if (!workspaceId || !branchId) return;
            if (useSession.getState().readOnly) return;
            const ws = useWorkspaceData.getState();
            if (stereoEdit.kind === "concept") {
              await ws.patchConcept(workspaceId, branchId, stereoEdit.anchorId, {
                stereotypes: selected,
              });
            } else {
              await ws.patchRelation(workspaceId, branchId, stereoEdit.anchorId, {
                stereotypes: selected,
              });
            }
            setStereoEdit(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * In-cell editor voor het naam/label-deel van een rij. Toont 't bewerkbare
 * fragment INLINE in de cel; rest van de cel (typeId-prefix, omhullende
 * haken) blijft vast zichtbaar als context.
 *
 * Commit-triggers:
 *  - Enter → commit
 *  - Tab → commit (zelfde gedrag als spreadsheet — focus-shift = commit)
 *  - blur → commit (klik buiten de cel)
 *  - Esc → cancel
 *
 * Klik op rij-niveau (achter de input) wordt door `stopPropagation` op
 * de input-events tegengehouden zodat dubbelklik en typen niet als
 * rij-selectie gelden.
 */
function CellEditor(props: {
  row: GridRow;
  buffer: string;
  onChange: (next: string) => void;
  onCommit: () => void;
  onCancel: () => void;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
}): JSX.Element {
  const { row, buffer, onChange, onCommit, onCancel, inputRef } = props;
  const prefix =
    row.kind === "concept" ? `${row.typeShort}(` : `${row.typeShort}(${row.sourceName}, `;
  const isLabelEdit = row.kind === "relation";
  // Voor relatie-label-edit: input staat tussen `target, "` en `")`.
  // Voor concept-name-edit: input staat tussen `Type(` en `)`.
  const beforeInput = isLabelEdit
    ? `${row.typeShort}(${row.sourceName}, ${row.targetName}${buffer === "" ? "" : ', "'}`
    : prefix;
  const afterInput = isLabelEdit ? (buffer === "" ? `)` : `")`) : `)`;
  return (
    <span className="inline-flex items-center gap-0">
      <span className="text-(--ld-muted)">{beforeInput}</span>
      <input
        ref={inputRef}
        type="text"
        value={buffer}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            onCommit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          }
          // Stop alle keystrokes om rij-shortcuts (DEL, etc.) niet te
          // triggeren tijdens edit.
          e.stopPropagation();
        }}
        onBlur={onCommit}
        onClick={(e) => e.stopPropagation()}
        className="min-w-[80px] rounded border border-(--ld-accent) bg-(--ld-surface) px-1 py-0 font-mono text-xs outline-none focus:border-(--ld-accent)"
        // Auto-width op de inhoud — value-length is een ruwe heuristic
        // (proportional font levert in Tailwind's default mono een
        // gemiddelde van ~7px per char op).
        style={{ width: `${Math.max(buffer.length, 4) * 7 + 16}px` }}
      />
      <span className="text-(--ld-muted)">{afterInput}</span>
    </span>
  );
}

/**
 * In-cell positie-editor voor kolom C op concept-rijen. Tekst-input
 * accepteert `x, y` (komma of spatie als separator). Op commit wordt
 * de string geparseerd en via `moveConceptOnCanvas` naar de store
 * geschreven; canvas-node verspringt direct mee. Op Esc cancellen we.
 */
function PositionEditor(props: {
  buffer: string;
  onChange: (next: string) => void;
  onCommit: () => void;
  onCancel: () => void;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
}): JSX.Element {
  const { buffer, onChange, onCommit, onCancel, inputRef } = props;
  return (
    <input
      ref={inputRef}
      type="text"
      value={buffer}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          onCommit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
        e.stopPropagation();
      }}
      onBlur={onCommit}
      onClick={(e) => e.stopPropagation()}
      placeholder="x, y"
      className="w-24 rounded border border-(--ld-accent) bg-(--ld-surface) px-1 py-0 font-mono text-xs outline-none focus:border-(--ld-accent)"
    />
  );
}

/**
 * In-cell handle-selector voor kolom C of D op relatie-rijen. Dropdown
 * met de vier zijden + `auto` (= wis expliciete handle, val terug op
 * React Flow's auto-routing). Selectie commit direct (geen blur-wachten);
 * Esc cancellen we via key-handler op de select.
 *
 * Auto-focus + auto-open lukt niet betrouwbaar cross-browser zonder
 * extra HTML-tricks — modelleur moet 'n keer klikken om 't menu te
 * openen. Acceptabel voor MVP.
 */
function HandleSelector(props: {
  buffer: string;
  onChange: (next: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const { buffer, onChange, onCancel } = props;
  const ref = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
    }
  }, []);
  return (
    <select
      ref={ref}
      value={buffer}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
        e.stopPropagation();
      }}
      onClick={(e) => e.stopPropagation()}
      // Geen `onBlur=onCancel`: native <select>-dropdowns kunnen
      // blur firen vóór change (browser-specifiek, racy met option-
      // selectie via mouseup). Dan zou onCancel het editing-record
      // wissen vóór de keuze gecommit kan worden, met als gevolg
      // dat de waarde "terugspringt" naar wat eerder gezet was.
      // Modelleur sluit nu met Esc of door een waarde te kiezen;
      // klik-buiten houdt 't dropdown open totdat één van die twee
      // gebeurt — kleine UX-trade-off voor correctheid.
      className="rounded border border-(--ld-accent) bg-(--ld-surface) px-1 py-0 font-mono text-xs outline-none focus:border-(--ld-accent)"
    >
      <option value="auto">auto</option>
      <option value="left">left</option>
      <option value="right">right</option>
      <option value="top">top</option>
      <option value="bottom">bottom</option>
    </select>
  );
}

/**
 * Modal voor multi-select-edit van stereotypes op één concept of relatie.
 * Wordt geopend door dubbelklik op de "Stereotypes"-cel in de tabel.
 * Toont alle stereotypes die applicabel zijn op het base-type (via
 * `reg.stereotypesFor(typeId)`), alfabetisch gesorteerd op label.
 * Op Opslaan wordt het concept/relatie gepatcht met de array-versie.
 *
 * Een ESC of klik buiten de modal sluit zonder opslaan. Een
 * "Wissen"-knop ruimt alle stereotypes in één klap weg.
 */
function StereotypeEditModal(props: {
  editingState: {
    anchorId: string;
    kind: "concept" | "relation";
    typeId: string;
    selected: Set<string>;
  };
  lang: "nl" | "en";
  onClose: () => void;
  onSave: (selected: string[]) => Promise<void> | void;
}) {
  const { editingState, lang, onClose, onSave } = props;
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set(editingState.selected));
  const [busy, setBusy] = useState(false);

  const available = useMemo(() => {
    const reg = getRegistry();
    // Bewust de OPGESLAGEN stereotypes (`editingState.selected`), niet de
    // live `selected`-state: conditionele `applies_to`-entries worden zo
    // één keer geëvalueerd bij het openen, in plaats van dat opties
    // verspringen terwijl de modelleur staat aan te vinken.
    const list = reg.stereotypesFor(editingState.typeId, [...editingState.selected]);
    return [...list].sort((a, b) =>
      resolveBilingual(a.label, lang).localeCompare(resolveBilingual(b.label, lang), lang),
    );
  }, [editingState.typeId, editingState.selected, lang]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      await onSave([...selected]);
    } finally {
      setBusy(false);
    }
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentationele overlay-laag; sluiten via Escape + knoppen, klik-buiten is muis-gemak
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className="flex max-h-[80vh] w-[880px] max-w-[95vw] flex-col overflow-hidden rounded-md border border-(--ld-control) bg-(--ld-surface) shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="border-(--ld-border) border-b bg-(--ld-raised) px-4 py-2 text-(--ld-text) text-sm">
          {t("gridPane.stereotypeModal.titlePrefix")}{" "}
          <span className="font-mono text-xs">{shortTypeId(editingState.typeId)}</span>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {available.length === 0 ? (
            <div className="px-3 py-4 text-center text-(--ld-muted) text-sm italic">
              {t("gridPane.stereotypeModal.noneAvailable")}
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              {available.map((st) => {
                const checked = selected.has(st.id);
                return (
                  <label
                    key={st.id}
                    className={`flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-(--ld-raised) ${
                      checked ? "text-(--ld-text)" : "text-(--ld-muted)"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(st.id)}
                      className="h-4 w-4"
                    />
                    <span className={checked ? "font-semibold" : ""}>
                      {resolveBilingual(st.label, lang)}
                    </span>
                    <span className="ml-auto font-mono text-xs text-(--ld-muted)">{st.id}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-(--ld-rule) border-t p-3">
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            disabled={busy || selected.size === 0}
            className="rounded px-3 py-1 text-(--ld-muted) text-sm hover:bg-(--ld-raised) disabled:opacity-40"
          >
            {t("gridPane.stereotypeModal.clear")}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded px-3 py-1 text-(--ld-muted) text-sm hover:bg-(--ld-raised)"
          >
            {t("gridPane.stereotypeModal.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className="rounded bg-(--ld-primary) px-3 py-1 text-sm text-(--ld-primary-ink) disabled:bg-(--ld-active)"
          >
            {busy ? t("gridPane.stereotypeModal.saving") : t("gridPane.stereotypeModal.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
